import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiFileText, FiSave } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const TeacherGradeSubmission = () => {
    const { submissionId } = useParams();
    const navigate = useNavigate();

    const [submission, setSubmission] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [marksGiven, setMarksGiven] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        const fetchSubmission = async () => {
            try {
                const res = await api.get(`/api/teacher/submissions/${submissionId}`);
                const { submission, questions } = res.data;
                setSubmission(submission);
                setQuestions(questions);

                let parsedAnswers = [];
                try {
                    parsedAnswers = typeof submission.answers === 'string' ? JSON.parse(submission.answers) : submission.answers;
                } catch (e) { }

                setAnswers(parsedAnswers || []);

                // Initialize marks
                const initialMarks = {};
                questions.forEach((q, i) => {
                    const ans = parsedAnswers[i] || {};
                    if (q.question_type === 'mcq') {
                        // Auto-calculate MCQ marks just for display
                        const selectedLetter = ans.selectedOption !== null && ans.selectedOption !== undefined
                            ? ['A', 'B', 'C', 'D'][ans.selectedOption]
                            : null;
                        initialMarks[i] = (selectedLetter === q.correct_option) ? q.marks : 0;
                    } else {
                        // If already graded manually, use that. Otherwise use the AI's pre-filled marks!
                        initialMarks[i] = ans.marksGiven !== undefined ? ans.marksGiven : (ans.marks_awarded !== undefined ? ans.marks_awarded : 0);
                    }
                });
                setMarksGiven(initialMarks);

            } catch (err) {
                console.error(err);
                alert(err.response?.data?.message || 'Failed to load submission.');
                navigate('/teacher/papers');
            } finally {
                setLoading(false);
            }
        };
        fetchSubmission();
    }, [submissionId, navigate]);

    const totalCalculatedScore = useMemo(() => {
        return Object.values(marksGiven).reduce((sum, val) => sum + (Number(val) || 0), 0);
    }, [marksGiven]);

    const totalMaxMarks = useMemo(() => {
        return questions.reduce((sum, q) => sum + q.marks, 0);
    }, [questions]);

    const handleMarkChange = (qIndex, value, maxMarks) => {
        let val = Number(value);
        if (isNaN(val) || val < 0) val = 0;
        if (val > maxMarks) val = maxMarks; // Prevent giving more than max marks

        setMarksGiven(prev => ({
            ...prev,
            [qIndex]: val
        }));
    };

    const handleSaveGrades = async () => {
        setShowConfirm(false);
        setSaving(true);
        try {
            const updatedAnswers = answers.map((ans, i) => ({
                ...ans,
                marksGiven: marksGiven[i]
            }));

            await api.put(`/api/teacher/submissions/${submissionId}/grade`, {
                finalScore: totalCalculatedScore,
                answers: updatedAnswers
            });
            alert('Grading saved successfully!');
            navigate(`/teacher/papers/${submission.paper_id}`);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to save grades.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontFamily: font }}>Loading submission details...</div>;
    if (!submission) return null;

    return (
        <div style={{ padding: '0px 10px 40px', fontFamily: font, maxWidth: 900, margin: '0 auto', animation: 'fadeIn 0.3s ease' }}>
            {/* Header / Nav */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <button onClick={() => navigate(`/teacher/papers/${submission.paper_id}`)} style={{ background: 'none', border: 'none', color: '#6366f1', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                    <FiArrowLeft /> Back to Paper
                </button>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ padding: '8px 16px', borderRadius: 12, background: C.bg, border: `1px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        Total Score: <span style={{ color: totalCalculatedScore >= (totalMaxMarks / 2) ? '#10b981' : '#f59e0b', fontSize: 18 }}>{totalCalculatedScore}</span> / {totalMaxMarks}
                    </div>
                    <button
                        onClick={(e) => { e.preventDefault(); setShowConfirm(true); }}
                        disabled={saving}
                        style={{ padding: '10px 20px', borderRadius: 12, background: '#6366f1', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, boxShadow: '0 4px 12px rgba(99,102,241,0.2)', position: 'relative', zIndex: 10 }}
                    >
                        {saving ? 'Saving...' : <><FiSave /> Save Evaluation</>}
                    </button>
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 20, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', marginBottom: 24 }}>
                <div style={{ padding: '24px 30px', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Evaluate Submission</h1>
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: submission.graded_at ? '#ecfdf5' : '#fffbeb', color: submission.graded_at ? '#10b981' : '#f59e0b', fontSize: 12, fontWeight: 700 }}>
                            {submission.graded_at ? 'Evaluated' : 'Pending Evaluation'}
                        </span>
                    </div>

                    {submission.invigilatorFileUrl && (
                        <div style={{ marginTop: 12, padding: '12px 16px', borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <FiFileText color="#4f46e5" size={20} />
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5', marginBottom: 2 }}>Invigilator Uploaded Answer Sheet</div>
                                <a href={submission.invigilatorFileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                                    Click here to view the scanned physical answer script
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <h2 style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: '0 0 16px' }}>Student Answers</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {questions.map((q, idx) => {
                    const ans = answers[idx] || {};
                    const isMCQ = q.question_type === 'mcq';
                    const maxMarks = q.marks;
                    let mcqCorrect = false;
                    let selectedLetter = null;

                    if (isMCQ) {
                        selectedLetter = ans.selectedOption !== null && ans.selectedOption !== undefined
                            ? ['A', 'B', 'C', 'D'][ans.selectedOption]
                            : null;
                        mcqCorrect = selectedLetter === q.correct_option;
                    }

                    return (
                        <div key={q.id} style={{ padding: 24, background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                            {/* Question Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                                        <span style={{ fontWeight: 800, fontSize: 15, color: '#6366f1', background: '#eef2ff', padding: '4px 10px', borderRadius: 8 }}>Q{q.question_number || idx + 1}</span>
                                        <span style={{ fontSize: 12, color: C.sub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{q.question_type}</span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: 16, color: C.text, lineHeight: 1.6, fontWeight: 500 }}>{q.question_text}</p>
                                    {q.image_url && (
                                        <div style={{ marginTop: 12, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: '#f8fafc' }}>
                                            <img
                                                src={`${api.defaults.baseURL}${q.image_url}`}
                                                alt="Question"
                                                style={{ maxWidth: '100%', maxHeight: 300, display: 'block', margin: '0 auto', cursor: 'zoom-in' }}
                                                onClick={(e) => { e.stopPropagation(); window.open(`${api.defaults.baseURL}${q.image_url}`, '_blank'); }}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, minWidth: 120 }}>
                                    <div style={{ fontSize: 13, color: C.sub, fontWeight: 600 }}>Max Marks: {maxMarks}</div>

                                    {/* Grading Box */}
                                    <div style={{ background: isMCQ ? '#f8fafc' : '#edfaf4', border: `1px solid ${isMCQ ? C.border : '#a7f3d0'}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Assign Marks</label>
                                        {isMCQ ? (
                                            <span style={{ fontSize: 18, fontWeight: 800, color: mcqCorrect ? '#10b981' : C.red }}>
                                                {marksGiven[idx]} <span style={{ fontSize: 14 }}>/ {maxMarks}</span>
                                            </span>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={maxMarks}
                                                    value={marksGiven[idx]}
                                                    onChange={(e) => handleMarkChange(idx, e.target.value, maxMarks)}
                                                    style={{ width: 50, textAlign: 'center', padding: '6px', fontSize: 16, fontWeight: 700, borderRadius: 6, border: `1px solid #a7f3d0`, outline: 'none' }}
                                                />
                                                <span style={{ fontSize: 14, color: C.sub, fontWeight: 600 }}>/ {maxMarks}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Answer Section */}
                            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', marginBottom: 12 }}>Student Answer</div>

                                {isMCQ ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        {['A', 'B', 'C', 'D'].map((opt, oIndex) => {
                                            const val = q[`option_${opt.toLowerCase()}`];
                                            if (!val) return null;

                                            const isSelected = selectedLetter === opt;
                                            const isCorrectAns = q.correct_option === opt;

                                            let bg = '#fff';
                                            let border = C.border;
                                            let icon = null;

                                            if (isSelected && isCorrectAns) {
                                                bg = '#ecfdf5'; border = '#10b981'; icon = <FiCheckCircle color="#10b981" size={18} />;
                                            } else if (isSelected && !isCorrectAns) {
                                                bg = '#fef2f2'; border = '#ef4444'; icon = <FiXCircle color="#ef4444" size={18} />;
                                            } else if (!isSelected && isCorrectAns) {
                                                bg = '#eef2ff'; border = '#6366f1'; icon = <FiCheckCircle color="#6366f1" size={18} opacity={0.5} />;
                                            }

                                            return (
                                                <div key={opt} style={{
                                                    padding: '12px 14px', borderRadius: 10, fontSize: 14,
                                                    background: bg, border: `2px solid ${border}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <span style={{ fontWeight: 800, width: 24, height: 24, borderRadius: 6, background: '#e2e8f0', color: C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{opt}</span>
                                                        <span style={{ fontWeight: isSelected ? 600 : 400, color: C.text }}>{val}</span>
                                                    </div>
                                                    {icon}
                                                </div>
                                            );
                                        })}
                                        {!selectedLetter && <div style={{ color: C.red, fontSize: 13, fontWeight: 600, gridColumn: 'span 2', marginTop: 8 }}>* Student did not select an answer.</div>}
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <div style={{
                                            padding: 16, background: '#fff', borderRadius: 8, border: `1px solid ${C.border}`,
                                            minHeight: 80, fontSize: 15, color: ans.answerText ? C.text : C.sub, whiteSpace: 'pre-wrap', lineHeight: 1.6
                                        }}>
                                            {ans.answerText || 'No text answer provided.'}
                                        </div>

                                        {ans.uploadedFileUrl && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ padding: '12px 16px', borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                                                    <FiFileText color="#4f46e5" size={20} />
                                                    <a href={ans.uploadedFileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>
                                                        View Uploaded Document ({q.upload_title || 'File'})
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        {!ans.answerText && !ans.uploadedFileUrl && (
                                            <div style={{ color: '#f59e0b', fontSize: 13, fontWeight: 600 }}>* Student left this question completely blank.</div>
                                        )}
                                        {ans.ai_feedback && (
                                            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#f5f3ff', border: '1px solid #ddd6fe', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                                <span style={{ fontSize: 16 }}>🤖</span>
                                                <div>
                                                    <div style={{ fontSize: 11, fontWeight: 800, color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 2 }}>AI Pre-Evaluation Suggestion</div>
                                                    <div style={{ fontSize: 13, color: '#5b21b6', fontWeight: 600 }}>{ans.ai_feedback}</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                input[type="number"] {
                    -moz-appearance: textfield;
                }
            `}</style>

            {/* Custom Confirm Modal */}
            {showConfirm && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20, animation: 'fadeIn 0.2s' }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: 24, maxWidth: 400, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', textAlign: 'center' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#eef2ff', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <FiSave size={32} />
                        </div>
                        <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: '#1e293b' }}>Finalize Evaluation?</h2>
                        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>By saving this evaluation, the final score will be permanently recorded and visible to the student.</p>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: '12px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleSaveGrades} style={{ flex: 1, padding: '12px', borderRadius: 12, border: 'none', background: '#6366f1', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.2)' }}>Yes, Save Score</button>
                        </div>
                    </div>
                </div>
            )}



        </div>
    );
};

export default TeacherGradeSubmission;
